/**
 * Minimal CSV parser covering papaparse options used in this app:
 * `{ header, skipEmptyLines, complete }`.
 */

function parseCsvRows(text) {
  const rows = []
  let row = []
  let cell = ''
  let inQuotes = false

  const pushCell = () => {
    row.push(cell)
    cell = ''
  }
  const pushRow = () => {
    // Ignore a trailing empty row from a final newline
    if (row.length === 1 && row[0] === '' && rows.length > 0) {
      row = []
      return
    }
    rows.push(row)
    row = []
  }

  const input = String(text ?? '').replace(/^\uFEFF/, '')
  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i]
    const next = input[i + 1]

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"'
        i += 1
      } else if (ch === '"') {
        inQuotes = false
      } else {
        cell += ch
      }
      continue
    }

    if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      pushCell()
    } else if (ch === '\n') {
      pushCell()
      pushRow()
    } else if (ch === '\r') {
      // handle CRLF / bare CR
      if (next === '\n') i += 1
      pushCell()
      pushRow()
    } else {
      cell += ch
    }
  }

  // last cell/row if file doesn't end with newline
  if (cell !== '' || row.length > 0) {
    pushCell()
    pushRow()
  }

  return rows
}

function isEmptyRow(row) {
  return !row.some((cell) => String(cell ?? '').trim() !== '')
}

/**
 * @param {string} input
 * @param {{ header?: boolean, skipEmptyLines?: boolean }} [options]
 * @returns {{ data: any[], meta: { fields?: string[] } }}
 */
export function parseCsv(input, { header = false, skipEmptyLines = false } = {}) {
  let rows = parseCsvRows(input)
  if (skipEmptyLines) rows = rows.filter((row) => !isEmptyRow(row))

  if (!header) {
    return { data: rows, meta: {} }
  }

  if (rows.length === 0) {
    return { data: [], meta: { fields: [] } }
  }

  const fields = rows[0].map((cell) => String(cell ?? ''))
  const data = rows.slice(1).map((row) => {
    const obj = {}
    for (let i = 0; i < fields.length; i += 1) {
      obj[fields[i]] = row[i] ?? ''
    }
    return obj
  })

  return { data, meta: { fields } }
}

/**
 * Drop-in for `Papa.parse` as used in this app.
 */
export function parse(input, options = {}) {
  const result = parseCsv(input, options)
  options.complete?.(result)
  return result
}

export default { parse }
